<script setup lang="ts">
  import { ref } from "vue";

  const {
    label,
    name,
    modelValue,
    description,
    placeholder = " ",
    variant = "stroked",
    disabled,
    required,
    maxlength,
  } = defineProps<{
    label?: string;
    description?: string;
    name?: string;
    modelValue?: string;
    placeholder?: string;
    variant?: "stroked" | "filled";
    required?: boolean;
    disabled?: boolean;
    maxlength?: number;
  }>();
  const emit = defineEmits<{
    "update:modelValue": [value: string];
  }>();

  const textareaRef = ref<HTMLTextAreaElement>(null);
  defineExpose({ field: textareaRef });

  const handleChange = (event: Event) => {
    emit("update:modelValue", (event.target as HTMLInputElement).value);
  };
</script>

<template>
  <div class="textarea">
    <label :for="name" class="textarea__label" v-if="label">
      {{ label }}
      <span v-if="required" class="textarea__label-required">*</span>
    </label>
    <div class="textarea__wrapper">
      <textarea
        ref="textareaRef"
        :name="name"
        :maxlength="maxlength"
        :class="['textarea__field', `textarea__field--${variant}`]"
        :value="modelValue"
        :placeholder="placeholder"
        @input="handleChange"
        :disabled="disabled"
        data-testid="textarea"
      />
    </div>
    <span v-if="description" class="textarea__description">{{ description }}</span>
  </div>
</template>

<style scoped>
  .textarea {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .textarea__wrapper {
    position: relative;
  }

  .textarea__wrapper > svg {
    stroke: var(--sf-base-icon-secondary);
  }

  .textarea__label,
  .textarea__description {
    font-size: 14px;
    line-height: 20px;
    color: var(--sf-base-text-secondary);
  }

  .textarea__label-required {
    color: var(--sf-base-text-error);
  }

  .textarea__icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
  }

  .textarea__field {
    padding: 9px 12px;
    width: 100%;
    border-radius: 8px;
    resize: none;
    outline: none;
    min-height: 200px;
  }

  .textarea__field:disabled {
    pointer-events: none;
  }

  .textarea__field::placeholder {
    color: var(--sf-base-text-tertiary);
  }

  .textarea__field:hover {
    border-color: var(--sf-base-border-dark);
  }

  .textarea__field:focus {
    border: 1px solid var(--sf-base-border-dark);
    background-color: var(--sf-base-background-secondary);
  }

  .textarea__field--stroked {
    background-color: var(--sf-base-background-white);
    border: 1px solid var(--sf-base-border-primary);
  }

  .textarea__field--stroked:focus {
    background-color: var(--sf-base-background-secondary);
    border-color: var(--sf-base-border-dark);
  }

  .textarea__field--filled {
    background-color: var(--sf-base-background-primary);
    border: 1px solid transparent;
  }

  .textarea__field--filled:focus {
    background-color: var(--sf-base-background-secondary);
    border: 1px solid var(--sf-base-border-dark);
  }
</style>
